"use client";

import { useEffect, useRef, type KeyboardEvent } from "react";
import Link from "next/link";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { GALLERY_SORT_OPTIONS, withGallerySortParam } from "@/lib/gallerySort";
import { withGallerySearchParam } from "@/lib/gallerySearch";
import { cn } from "@/lib/utils";
import type { DressType, GallerySort } from "@/types/gallery";

interface GalleryMobileCategory {
  id: DressType;
  shortLabel: string;
  href: string;
}

interface GalleryMobileFiltersProps {
  activeCategory: DressType;
  categories: GalleryMobileCategory[];
  search: string;
  sort: GallerySort;
  sortCompactLabels: Record<GallerySort, string>;
}

export default function GalleryMobileFilters({
  activeCategory,
  categories,
  search,
  sort,
  sortCompactLabels,
}: GalleryMobileFiltersProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const galleryRootHref =
    activeCategory === "all" ? "/gallery" : `/gallery/${activeCategory}`;

  const closeFilters = () => {
    if (detailsRef.current) detailsRef.current.open = false;
  };

  // Native <details> only closes from its summary; close it on any pointer
  // press outside the popover too, so it never stays over the grid.
  useEffect(() => {
    const details = detailsRef.current;
    if (!details) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!details.contains(event.target as Node)) {
        details.open = false;
      }
    };
    const handleToggle = () => {
      if (details.open) {
        document.addEventListener("pointerdown", handlePointerDown);
      } else {
        document.removeEventListener("pointerdown", handlePointerDown);
      }
    };

    details.addEventListener("toggle", handleToggle);
    return () => {
      details.removeEventListener("toggle", handleToggle);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDetailsElement>) => {
    if (event.key !== "Escape" || !detailsRef.current?.open) return;

    event.preventDefault();
    closeFilters();
    detailsRef.current.querySelector("summary")?.focus();
  };

  return (
    <details
      ref={detailsRef}
      className="relative shrink-0 lg:hidden"
      onKeyDown={handleKeyDown}
    >
      <summary className="flex h-11 cursor-pointer list-none items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
        <SlidersHorizontal className="size-3.5" aria-hidden />
        <span className="whitespace-nowrap">Filter & sort</span>
        <ChevronDown className="size-3.5" aria-hidden />
      </summary>
      <div className="absolute right-0 top-11 z-50 w-[min(86vw,304px)] max-h-[60dvh] overflow-y-auto overscroll-contain rounded-xl border border-border bg-card p-2.5 shadow-xl">
        <div>
          <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Dress type
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {categories.map(category => {
              const isActive = activeCategory === category.id;
              return (
                <Link
                  key={category.id}
                  href={withGallerySearchParam(
                    withGallerySortParam(category.href, sort),
                    search
                  )}
                  prefetch={false}
                  aria-current={isActive ? "page" : undefined}
                  onClick={closeFilters}
                  className={cn(
                    "flex min-h-11 items-center justify-center rounded-lg border px-2 text-center text-sm font-medium transition-colors",
                    isActive
                      ? "border-transparent bg-muted text-foreground"
                      : "border-border bg-background/50 text-muted-foreground hover:text-foreground"
                  )}
                >
                  {category.shortLabel}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="mt-3 border-t border-border pt-2.5">
          <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sort
          </p>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {GALLERY_SORT_OPTIONS.map(option => {
              const isActive = option.value === sort;
              return (
                <Link
                  key={option.value}
                  href={withGallerySearchParam(
                    withGallerySortParam(galleryRootHref, option.value),
                    search
                  )}
                  prefetch={false}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={`Sort by ${option.label}`}
                  onClick={closeFilters}
                  className={cn(
                    "flex min-h-11 items-center justify-center rounded-lg px-2 text-center text-sm font-medium transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
                  )}
                >
                  {sortCompactLabels[option.value]}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </details>
  );
}
